import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  Loader2,
  Server,
  ShieldCheck,
} from "lucide-react";

import styles from "./LosApiDocs.module.scss";

const OPENAPI_URL = "https://api-los.doql.com/openapi.json";

const METHOD_ORDER = [
  "get",
  "post",
  "put",
  "patch",
  "delete",
  "options",
  "head",
];

const refName = (ref) => (typeof ref === "string" ? ref.split("/").pop() : "");

// The longest leading path shared by EVERY endpoint, returned as segments.
// e.g. ["/api/legalai/qc/extract", "/api/legalai/qc/extractions/{id}"]
//   -> ["api", "legalai", "qc"]  ->  common base "/api/legalai/qc".
const commonPrefixSegments = (allPaths) => {
  if (!allPaths.length) return [];
  const split = allPaths.map((p) => String(p).split("/").filter(Boolean));
  const first = split[0];
  const prefix = [];
  for (let i = 0; i < first.length; i += 1) {
    const segment = first[i];
    if (split.every((segs) => segs[i] === segment)) prefix.push(segment);
    else break;
  }
  return prefix;
};

// Best-effort human-readable type for a schema (without full $ref resolution).
const schemaType = (schema) => {
  if (!schema) return "—";
  if (schema.$ref) return refName(schema.$ref);
  if (schema.type === "array") {
    const items = schema.items || {};
    const inner = items.$ref ? refName(items.$ref) : items.type || "object";
    return `array<${inner}>`;
  }
  if (schema.enum) return "enum";
  return schema.type || "object";
};

const LosApiDocs = () => {
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(OPENAPI_URL, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const json = await res.json();
        if (active) setSpec(json);
      } catch (err) {
        if (active) {
          setError(
            err?.message ||
              "Failed to load the LOS API specification. Please try again.",
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className={styles["los-docs__state"]}>
        <Loader2 className={styles["los-docs__spin"]} size={28} />
        <span>Loading LOS API specification…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`${styles["los-docs__state"]} ${styles["los-docs__state--error"]}`}
      >
        <AlertTriangle size={28} strokeWidth={2.2} />
        <span>{error}</span>
      </div>
    );
  }

  if (!spec) return null;

  const info = spec.info || {};
  const servers = Array.isArray(spec.servers) ? spec.servers : [];
  const paths = spec.paths || {};

  // Flatten { path: { method: op } } into an ordered list of operations.
  const operations = [];
  Object.keys(paths).forEach((path) => {
    const item = paths[path] || {};
    METHOD_ORDER.forEach((method) => {
      if (item[method]) operations.push({ path, method, op: item[method] });
    });
  });

  // Single common base path shared by all endpoints (e.g. "/api/legalai/qc").
  // It becomes the group heading; each endpoint is shown by its suffix.
  const prefixSegments = commonPrefixSegments(operations.map((o) => o.path));
  const commonBase = prefixSegments.length
    ? "/" + prefixSegments.join("/")
    : "";
  const relativePath = (path) => {
    const rest = String(path)
      .split("/")
      .filter(Boolean)
      .slice(prefixSegments.length);
    return "/" + rest.join("/");
  };

  return (
    <div className={styles["los-docs"]}>
      <div className={styles["los-docs__panel"]}>
        {/* HEADER */}
        <div className={styles["los-docs__header"]}>
        <div className={styles["los-docs__header-icon"]}>
          <ShieldCheck size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["los-docs__header-main"]}>
          <div className={styles["los-docs__header-top"]}>
            <h2 className={styles["los-docs__header-title"]}>
              Legal Quality Check
            </h2>
            {/* {info.version && (
              <span className={styles["los-docs__version"]}>v{info.version}</span>
            )} */}
          </div>
          {/* {info.description && (
            <p className={styles["los-docs__header-desc"]}>
              {info.description}
            </p>
          )} */}

          <div className={styles["los-docs__header-meta"]}>
            {commonBase && (
              <span className={styles["los-docs__header-path"]}>
                https://api-los.doql.com{commonBase}
              </span>
            )}
            <span className={styles["los-docs__header-count"]}>
              {operations.length} endpoint{operations.length === 1 ? "" : "s"}
            </span>
          </div>

          {servers.length > 0 && (
            <div className={styles["los-docs__servers"]}>
              {servers.map((s, i) => (
                <span key={i} className={styles["los-docs__server"]}>
                  <Server size={13} strokeWidth={2.5} />
                  {s.url}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

        {/* ENDPOINTS */}
        <div className={styles["los-docs__group-ops"]}>
          {operations.map(({ path, method, op }, idx) => (
            <details
              key={`${method}-${path}-${idx}`}
              className={styles["los-docs__op"]}
            >
              <summary className={styles["los-docs__op-summary"]}>
                <span
                  className={`${styles["los-docs__method"]} ${
                    styles[`los-docs__method--${method}`]
                  }`}
                >
                  {method.toUpperCase()}
                </span>
                <span className={styles["los-docs__path"]}>
                  {relativePath(path)}
                </span>
                {op.summary && (
                  <span className={styles["los-docs__op-title"]}>
                    {op.summary}
                  </span>
                )}
                <ChevronDown
                  size={16}
                  className={styles["los-docs__chevron"]}
                />
              </summary>

              <div className={styles["los-docs__op-body"]}>
                {op.description && (
                  <p className={styles["los-docs__desc"]}>{op.description}</p>
                )}

                {/* Parameters */}
                {Array.isArray(op.parameters) && op.parameters.length > 0 && (
                  <div className={styles["los-docs__block"]}>
                    <h4 className={styles["los-docs__block-title"]}>
                      Parameters
                    </h4>
                    <table className={styles["los-docs__table"]}>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>In</th>
                          <th>Required</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {op.parameters.map((p, i) => (
                          <tr key={i}>
                            <td className={styles["los-docs__mono"]}>
                              {p.name}
                            </td>
                            <td>{p.in}</td>
                            <td>{p.required ? "Yes" : "No"}</td>
                            <td className={styles["los-docs__mono"]}>
                              {schemaType(p.schema)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Request body */}
                {op.requestBody?.content && (
                  <div className={styles["los-docs__block"]}>
                    <h4 className={styles["los-docs__block-title"]}>
                      Request Body
                    </h4>
                    <div className={styles["los-docs__chips"]}>
                      {Object.keys(op.requestBody.content).map((ct) => {
                        const schema = op.requestBody.content[ct]?.schema;
                        return (
                          <span key={ct} className={styles["los-docs__chip"]}>
                            {ct}
                            {schema ? ` · ${schemaType(schema)}` : ""}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Responses */}
                {op.responses && (
                  <div className={styles["los-docs__block"]}>
                    <h4 className={styles["los-docs__block-title"]}>
                      Responses
                    </h4>
                    <table className={styles["los-docs__table"]}>
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(op.responses).map((code) => (
                          <tr key={code}>
                            <td className={styles["los-docs__mono"]}>{code}</td>
                            <td>{op.responses[code]?.description || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </details>
          ))}
        </div>
      </div>

      {operations.length === 0 && (
        <div className={styles["los-docs__state"]}>
          No endpoints found in the specification.
        </div>
      )}

      {/* Raw spec fallback for anything not surfaced above */}
      <details className={styles["los-docs__raw"]}>
        <summary className={styles["los-docs__raw-summary"]}>
          View raw OpenAPI JSON
        </summary>
        <pre className={styles["los-docs__pre"]}>
          {JSON.stringify(spec, null, 2)}
        </pre>
      </details>
    </div>
  );
};

export default LosApiDocs;
